import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'

import * as assistant from '../assistant'
import * as me from '../me'

import { Groups } from './groups'

const ASSISTANT_SELECTION_STORAGE_NAME = 'AssistantSelectionSessionStorage'
const SELECTED_AGENT_ID_STORAGE_KEY = `${ASSISTANT_SELECTION_STORAGE_NAME}:selectedAgentId`

/**
 * Reads the persisted assistant selection before the store-backed storage
 * feature finishes its asynchronous initialization.
 */
function readStoredSelectedAgentId(): ents.core.agent.Agent.Id | undefined {
  const storedJson = window.sessionStorage.getItem(
    SELECTED_AGENT_ID_STORAGE_KEY,
  )
  if (TypeChecker.isNullish(storedJson)) {
    return undefined
  }

  try {
    const stored: unknown = JSON.parse(storedJson)
    return TypeChecker.isString(stored) && ents.core.agent.Agent.Id.isId(stored)
      ? stored
      : undefined
  } catch {
    return undefined
  }
}

/**
 * Injectable service that maps backend-driven agent assignments to
 * entity-backed Group instances for the slideout.
 *
 * Also manages the currently selected assistant and derived state
 * (selected group, filtered assistant displays) so that child components
 * can inject this service directly instead of going through a
 * ComponentStore middleman.
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Assistants {
  /**
   * Browser-session storage for the user's explicit assistant choice.
   */
  private readonly _selectionStorage =
    new store.storage.included.SessionStorage({
      name: ASSISTANT_SELECTION_STORAGE_NAME,
      properties: {
        selectedAgentId: store.storage.property<
          ents.core.agent.Agent.Id | undefined
        >(),
      },
    })

  /** Current-user context and agent assignment source. */
  private readonly _meFeature = ngCore.inject(me.Feature)

  /** Resolves entity display groups to conversational assistant groups. */
  private readonly _groups = ngCore.inject(Groups)

  /**
   * Owns ephemeral, externally-supplied slideout session context (e.g. DMS
   * records provided by a future mosaic action).
   */
  private readonly _sessionContextFeature = ngCore.inject(
    assistant.sessionContext.Feature,
  )

  /** Whether the initial resolved app context has been observed. */
  private _hasInitialAppContext = false

  /**
   * The set of agent IDs the current user is assigned to on this page.
   * Used downstream to filter inner assistants within Groups.
   */
  public readonly assignedAgentIds =
    this._meFeature.contexts.selectors.currentAgents.project(
      agents => new Set(agents.map(a => a.id)),
    )

  /**
   * Available assistant display groups for the current page.
   * This stays metadata-only so the launcher can render without creating
   * entity-backed conversational Groups.
   */
  public readonly availableAssistantDisplayGroups =
    this._meFeature.contexts.selectors.currentAgents.project(
      (agents): readonly ents.core.agent.Display.Group[] =>
        ents.core.agent.Display.Group.getVisibleForAgentIds(
          agents.map(agent => agent.id),
        ).filter(
          displayGroup =>
            displayGroup.assistantKind ===
            ents.core.agent.Display.Kind.GenericChat,
        ),
    )

  /**
   * Available Groups for the current page.
   * Maps assigned Agent.ForUser entries through entity display groups into
   * conversational Group instances.
   * Deduplicates so each Group appears at most once.
   */
  public readonly availableConversationalGroups =
    this.availableAssistantDisplayGroups.project(
      (displayGroups): readonly assistant.conversational.Group[] => {
        const result: assistant.conversational.Group[] = []

        for (const displayGroup of displayGroups) {
          const group = this._groups.get(displayGroup)

          if (group && !result.includes(group)) {
            result.push(group)
          }
        }

        return result
      },
    )

  /**
   * Default group = first available.
   */
  public readonly defaultAssistantDisplayGroupId =
    this.availableAssistantDisplayGroups.project(
      (available): string | undefined =>
        available.length > 0 ? available[0].id : undefined,
    )

  // ===== Selection State =====

  /**
   * ID of the currently selected agent.
   */
  public readonly selectedAgentIdSg = ngCore.signal<
    ents.core.agent.Agent.Id | undefined
  >(readStoredSelectedAgentId())

  /**
   * Whether the Assistant slideout is open.
   */
  public readonly assistantSlideoutOpenSg = ngCore.signal(false)

  /** The conversational group that contains the selected agent. */
  public readonly selectedConversationalGroupSg = ngCore.computed(() => {
    if (!this.assistantSlideoutOpenSg()) {
      return undefined
    }

    const selectedAgentId = this.selectedAgentIdSg()
    return this.getConversationalGroupByAgentId(selectedAgentId)
  })

  /**
   * Flattened list of all assistant displays from all groups,
   * filtered by assigned agent IDs when provided.
   */
  public readonly availableAssistantDisplaysSg = ngCore.computed<
    ents.core.agent.Display[]
  >(() => {
    const displayGroups = this.availableAssistantDisplayGroups.asSignal()
    const assignedAgentIds = this.assignedAgentIds.asSignal()
    return displayGroups
      .flatMap(displayGroup => {
        const assistantDisplays = displayGroup.agents
        if (!assignedAgentIds) {
          return assistantDisplays
        }
        return assistantDisplays.filter(assistantDisplay =>
          assignedAgentIds.has(assistantDisplay.id),
        )
      })
      .filter(TypeChecker.isNotNullish.raw)
  })

  /**
   * Display metadata for the currently selected assistant.
   */
  public readonly selectedAssistantDisplaySg = ngCore.computed(() =>
    this.availableAssistantDisplaysSg().find(
      assistantDisplay => assistantDisplay.id === this.selectedAgentIdSg(),
    ),
  )

  /**
   * Externally-supplied knowledge-base retrieval records for the current
   * session, if any were provided for the currently selected agent.
   * Automatically ignored (returns `undefined`) once the user switches to
   * a different agent than the one the context was opened for.
   */
  public readonly activeSessionKnowledgeBaseRetrievalRecordsSg =
    ngCore.computed(
      (context = this._sessionContextFeature.selectors.context.asSignal()) =>
        context && context.agentId === this.selectedAgentIdSg()
          ? context.records
          : undefined,
    )

  /**
   * Whether the selected agent assignment allows context configuration.
   */
  public readonly selectedAgentCanConfigureFunctionalitySg = ngCore.computed(
    () => {
      const selectedAssistantDisplay = this.selectedAssistantDisplaySg()
      const currentAgents =
        this._meFeature.contexts.selectors.currentAgents.asSignal()
      const currentUser = this._meFeature.selectors.currentUser.asSignal()

      return currentAgents.some(agent => {
        if (agent.id !== selectedAssistantDisplay?.id) {
          return false
        }

        if (
          !ents.core.admin.Access.allows(
            agent.access,
            ents.core.admin.Access.Flag.CanUse,
          )
        ) {
          return false
        }

        if (currentUser?.isPlatformAdmin === true) {
          return true
        }

        return ents.core.admin.Access.allows(
          agent.access,
          ents.core.admin.Access.Flag.CanConfigureFunctionality,
        )
      })
    },
  )

  /**
   * ID of the currently selected conversation within the current group, if any.
   * Returns undefined for one-off assistants or when no conversation is selected.
   */
  public readonly currentConversationIdSg = ngCore.computed(() => {
    const group = this.selectedConversationalGroupSg()
    return group?.conversation.plugins.currentEntityPlugin.feature.selectors
      .getId()
      .asSignal() as ents.core.agent.Conversation.Id | undefined
  })

  /**
   * Clamp the active selection to an accessible assistant display whenever
   * availability changes.  Handles both the initial "nothing selected"
   * case and the case where the previously-selected agent was
   * filtered out (e.g. the user only has projectRecommendation inside
   * MosaicTreeEditor).
   */
  private readonly _clampSelectionEffect = ngCore.effect(() => {
    const assistantDisplays = this.availableAssistantDisplaysSg()
    const selectedAgentId = this.selectedAgentIdSg()

    if (!assistantDisplays.length) {
      return
    }

    const currentIsAccessible =
      TypeChecker.isNotNullish(selectedAgentId) &&
      assistantDisplays.some(
        assistantDisplay => assistantDisplay.id === selectedAgentId,
      )

    if (!currentIsAccessible) {
      this.selectedAgentIdSg.set(assistantDisplays[0]?.id)
    }
  })

  /**
   * App context changes should behave like a fresh app entry: close the
   * slideout and remove the persisted assistant choice.
   */
  private readonly _clearSelectionOnAppContextEffect = ngCore.effect(() => {
    const appContext = this._meFeature.contexts.selectors.appContext.asSignal()

    if (!this._hasInitialAppContext) {
      this._hasInitialAppContext = TypeChecker.isNotNullish(appContext)
      return
    }

    this.assistantSlideoutOpenSg.set(false)
    this.selectedAgentIdSg.set(undefined)
    this._selectionStorage.actions.setSelectedAgentId
      .from('Assistant.AppContext.Changed')
      .dispatch(undefined)
  })

  /**
   * Clears externally-supplied session context once the slideout closes.
   */
  private readonly _clearSessionContextEffect = ngCore.effect(() => {
    if (!this.assistantSlideoutOpenSg()) {
      this._sessionContextFeature.actions.clear
        .from('Assistants.SlideoutClosed')
        .dispatch()
    }
  })

  /**
   * Keep group's internal selected agent ID in sync with selection.
   */
  private readonly _syncGroupAgentIdEffect = ngCore.effect(() => {
    const currentGroup = this.selectedConversationalGroupSg()

    if (currentGroup) {
      const selectedAgentId = this.selectedAgentIdSg()

      if (
        TypeChecker.isNotNullish(selectedAgentId) &&
        currentGroup.selectedAgentIdSg() !== selectedAgentId
      ) {
        ents.core.agent.Agent.Id.assertIsId(selectedAgentId)
        currentGroup.selectedAgentIdSg.set(selectedAgentId)
      }
    }
  })

  /**
   * Opens the assistant slideout for the given agent with externally-
   * supplied knowledge-base retrieval context (e.g. from a mosaic action).
   * The context is automatically cleared once the slideout is closed.
   */
  public openWithKnowledgeBaseContext(
    agentId: ents.core.agent.Agent.Id,
    records: readonly ents.core.agent.KnowledgeBase.Retrieval.Record[],
  ): void {
    this.selectAgent(agentId)
    this.assistantSlideoutOpenSg.set(true)
    this._sessionContextFeature.actions.open
      .from('Assistants.openWithKnowledgeBaseContext')
      .dispatch({ agentId, records })
  }

  /**
   * Selects an assistant and stores that explicit choice for this browser
   * session.
   */
  public selectAgent(agentId: ents.core.agent.Agent.Id | undefined): void {
    const selectedAgentId = this.availableAssistantDisplaysSg().some(
      assistantDisplay => assistantDisplay.id === agentId,
    )
      ? agentId
      : undefined

    this.selectedAgentIdSg.set(selectedAgentId)
    this._selectionStorage.actions.setSelectedAgentId
      .from('Assistant.Selection.Changed')
      .dispatch(selectedAgentId)
  }

  /**
   * Materializes the conversational group for a specific agent only when
   * the UI needs that agent's assistant collections.
   */
  public getConversationalGroupByAgentId(
    agentId: ents.core.agent.Agent.Id | undefined,
  ): assistant.conversational.Group | undefined {
    if (!ents.core.agent.Agent.Id.isId(agentId)) {
      return undefined
    }

    const displayGroup = this.availableAssistantDisplayGroups
      .asSignal()
      .find(group =>
        group.agents.some(assistantDisplay => assistantDisplay.id === agentId),
      )

    if (!displayGroup) {
      return undefined
    }

    return this._groups.get(displayGroup)
  }
}
